import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MenuAction } from '@aiwyn/foundations/models';
import {
  EncryptApiService,
  INVOICES_TABLE_FILTER_OPTIONS,
  Invoice,
  InvoiceStatus,
  InvoiceTotals,
  InvoiceViewModel,
  InvoicesApiService,
} from '@anduin/api';
import { AuthService, FirmPortalPermission, PermissionsState } from '@anduin/auth';
import {
  AgGridCellTemplateRendererComponent,
  AgGridCellTooltipOverflowComponent,
  AgGridCellWithCheckboxComponent,
  AgGridFactory,
  AgGridFilterAggregate,
  AgGridFilterModel,
  AgGridStateService,
  AgGridValueFormatters,
  AgGridZeroStateMessages,
  BridgeMarshalledDatasource,
  DATE_COLUMN_RIGHT_ALIGNED,
  DEFAULT_AG_GRID_ZERO_STATE_MESSAGES,
  EXCEL_STYLES,
  EXCEL_STYLE_CURRENCY,
  EXCEL_STYLE_DATE_MIDDLE_ENDIAN,
  EXCEL_STYLE_TEXT,
  EXCLUDE_COLUMN_FROM_EXPORT,
  HasuraBaseQuery,
  LoadableStatus,
  MessageService,
  NUMBER_COLUMN,
  PinnedSide,
  PluralizePipe,
  WhereAggregateOperator,
  addCellClassRules,
  applicationDebounceTime,
  base64ToArrayBuffer,
  downloadFileFromUrl,
  extractFailureContext,
  generateUrlFromBlob,
  ignoreNullish,
  millisecondsToDays,
  openPdfInNewTab,
} from '@anduin/shared';
import { Store } from '@ngxs/store';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  ColumnApi,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { Apollo } from 'apollo-angular';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, pairwise, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BillingPermissionsService } from 'src/app/features/billing/services/billing-permissions.service';
import { DrawerService } from 'src/app/shared/components/side-drawer/side-drawer.service';
import { InvoiceReversalDialogService } from '../../../accounts-receivable/components/dialogs/invoice-reversal-dialog.service';
import { InvoicesStep, InvoicesTrackService } from '../../tracking';
import { resolveInvoiceStatus } from '../../utils';
import { ResendInvoiceDialogComponent } from '../resend-invoice-dialog/resend-invoice-dialog.component';
import { InvoicesDialogsService } from './invoices-dialogs.service';

const DATE_FORMAT = 'MM/DD/yyyy';

@Component({
  selector: 'invoices-table',
  templateUrl: './invoices-table.component.html',
  styleUrls: ['./invoices-table.component.scss'],
  standalone: false,
})
export class InvoicesTableComponent implements OnInit, OnDestroy {
  private _unsubscribe$ = new Subject<void>();
  private _agGridStateService: AgGridStateService;
  public InvoicesTableFilterOptions = INVOICES_TABLE_FILTER_OPTIONS;

  @Input() query: HasuraBaseQuery;
  @Input() datasourceMarshallFn: (invoice: Invoice) => InvoiceViewModel;
  @Input() id: string;
  @Input() searchValue$: Observable<string>;
  @Input() invoicesFilter$: Observable<INVOICES_TABLE_FILTER_OPTIONS>;
  @Input() invoiceTotals$: Observable<InvoiceTotals>;
  @Input() canLaunchClientPortal$: Observable<boolean>;
  @Input() updateSearchValue: (searchValue: string) => void;
  @Input() updateFilterSelection: (filter: INVOICES_TABLE_FILTER_OPTIONS) => void;
  @Input() noRowsMessages: AgGridZeroStateMessages = DEFAULT_AG_GRID_ZERO_STATE_MESSAGES;
  @Output() reverse = new EventEmitter<void>();
  datasource: BridgeMarshalledDatasource<Invoice, InvoiceViewModel>;

  canViewInvoiceDetails = this.store.selectSignal(PermissionsState.hasPermission([FirmPortalPermission.PAYMENTS_CAN_VIEW_INVOICE_DETAILS]));
  canResendInvoices = this.store.selectSignal(PermissionsState.hasPermission([FirmPortalPermission.PAYMENTS_CAN_RESEND_INVOICES]));
  canViewBilling = this.store.selectSignal(PermissionsState.hasPermission([FirmPortalPermission.CAN_VIEW_BILLING]));
  hasReverseInvoicePermission = this.billingPermissions.canReverseInvoice;

  // Selection state for bulk actions
  readonly selectedInvoices = signal<InvoiceViewModel[]>([]);
  readonly selectedText = computed(() => `${this.pluralize.transform(this.selectedInvoices().length, 'invoice')} selected`);
  readonly toolbarVisible = computed(() => this.selectedInvoices().length > 0);

  // Toolbar actions for batch actions toolbar
  readonly toolbarActions: MenuAction<InvoiceViewModel[]>[] = [
    {
      icon: 'download',
      label: (invoices) => {
        const downloadable = invoices.filter((i) => i.invoiceFileId).length;
        return `Download ${downloadable} Invoice${downloadable !== 1 ? 's' : ''}`;
      },
      disabled: (invoices) => invoices.every((i) => !i.invoiceFileId),
      tooltip: (invoices) => {
        const unavailable = invoices.filter((i) => !i.invoiceFileId).length;
        if (unavailable === invoices.length) return 'The PDF for the selected invoice(s) is not available in Aiwyn.';
        if (unavailable > 0) return `${unavailable} of ${invoices.length} selected invoice(s) have no PDF and will be skipped.`;
        return undefined;
      },
      onClick: (invoices) => this.handleDownloadAction(invoices),
    },
  ];

  public activeFilter$ = new BehaviorSubject<INVOICES_TABLE_FILTER_OPTIONS>(null);
  @ViewChild(AgGridAngular) agGrid: AgGridAngular;
  @ViewChild('actionsTemplate') actionsTemplate: TemplateRef<HTMLElement>;
  @ViewChild('createPaymentDrawer') createPaymentDrawerTemplate: TemplateRef<unknown>;

  private readonly drawerService = inject(DrawerService);
  private readonly viewContainerRef = inject(ViewContainerRef);

  readonly drawerInvoiceId = signal<number>(0);
  readonly drawerClientId = signal<number>(0);
  readonly drawerOutstandingAmount = signal<number>(0);
  readonly drawerCurrencyCode = signal<string | undefined>(undefined);
  readonly drawerInvoiceNumber = signal<string>('');

  private getInvoiceAmount(invoice: InvoiceViewModel): number {
    return invoice.amountInCurrency ?? invoice.amount;
  }

  invoiceCanBeReversed(rowData: InvoiceViewModel): boolean {
    const invoiceAmount = this.getInvoiceAmount(rowData);

    // Allow reversal if invoice is $0 (regardless of payment status or draft bill)
    // OR if invoice is unpaid AND has an associated draft bill
    return invoiceAmount === 0 || (!rowData.isPaid && !!rowData.draftBill);
  }

  reverseInvoiceState(rowData: InvoiceViewModel): 'no-permission' | 'paid' | 'not-reversible' | 'ok' {
    if (!this.hasReverseInvoicePermission()) {
      return 'no-permission';
    }
    if (!this.invoiceCanBeReversed(rowData)) {
      return rowData.isPaid ? 'paid' : 'not-reversible';
    }
    return 'ok';
  }

  reverseInvoiceTooltip(rowData: InvoiceViewModel): string {
    switch (this.reverseInvoiceState(rowData)) {
      case 'no-permission':
        return 'You do not have permission to reverse invoices.';
      case 'paid':
        return 'This invoice has been paid and cannot be reversed. Remove the payment first to enable reversal.';
      case 'not-reversible':
        return 'This invoice has no associated draft bill and cannot be reversed.';
      case 'ok':
        return '';
    }
  }

  currentStaffId: number;
  searchBarControl = new FormControl<string>('', { nonNullable: true });
  setSearchValue$ = this.searchBarControl.valueChanges.pipe(
    applicationDebounceTime(),
    tap((searchValue) => this.updateSearchValue(searchValue)),
    takeUntil(this._unsubscribe$),
  );
  protected columnFilterApplied$ = new Subject<boolean>();
  protected onModelUpdated$ = new Subject<void>();
  protected gridApi$ = new BehaviorSubject<GridApi>(null);
  protected columnApi$ = new BehaviorSubject<ColumnApi>(null);
  initializedGridApis$ = combineLatest([
    this.gridApi$.asObservable().pipe(filter<GridApi>(Boolean)),
    this.columnApi$.asObservable().pipe(filter<ColumnApi>(Boolean)),
  ]);

  noRowsOverlayMessageSubject$ = new BehaviorSubject('');
  noRowsOverlayMessage$: Observable<void>;

  private agGridFactory: AgGridFactory = new AgGridFactory();
  private lastFilterModel: Record<string, unknown> = {};
  private lastSortState: Record<string, 'asc' | 'desc' | null> = {};
  // Filter/sort handlers fire during state-restore on first render; only user-initiated
  // changes after `onFirstDataRendered` should reach analytics.
  private trackingArmed = false;
  // ag-grid 28 has no `event.source` on SelectionChangedEvent — armed before programmatic
  // deselectAll so the resulting event doesn't double-emit alongside SELECTION_CLEARED.
  private suppressProgrammaticSelection = false;
  gridOptions: GridOptions = {
    ...this.agGridFactory.serverSideGridOption({ excelStyles: EXCEL_STYLES }),
    excelStyles: EXCEL_STYLES,
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    onFirstDataRendered: () => {
      this.trackingArmed = true;
    },
    onFilterChanged: (event: FilterChangedEvent) => this.onFilterChanged(event),
    noRowsOverlayComponentParams: {
      asyncMessage$: this.noRowsOverlayMessageSubject$,
      headerRowsCount: 1,
    },
    onModelUpdated: () => this.onModelUpdated$.next(),
    onSelectionChanged: (event: SelectionChangedEvent) => this.onSelectionChanged(event),
    onSortChanged: (event: SortChangedEvent) => this.onSortChanged(event),
    suppressRowHoverHighlight: true,
    getRowId: ({ data }) => `${data.id}`,
  };

  viewDraftBillSubject$ = new Subject<number>();
  viewDraftBill$ = this.viewDraftBillSubject$.asObservable().pipe(
    switchMap((draftBillId) =>
      this.router.navigate(['billing', 'loadDraftBill'], { queryParams: { draftId: draftBillId }, queryParamsHandling: 'merge' }),
    ),
    tap(
      (_) => {},
      (_) => this.messageService.error('There was an error showing the draft invoice.'),
    ),
    takeUntil(this._unsubscribe$),
  );

  viewInvoicePdfSubject$ = new BehaviorSubject<number>(null);
  viewInvoicePdf$ = this.viewInvoicePdfSubject$.asObservable().pipe(
    filter((invoiceId) => invoiceId != null),
    take(1),
    // Both switchMaps will complete on success or failure, so there's no need for another take(1).
    switchMap((invoiceId) => this.invoicesApiService.getInvoicePDF(`${invoiceId}`)),
    tap(
      (_) => {},
      (_) => this.messageService.error('There was an error retrieving the invoice PDF.'),
    ),
    switchMap(
      (invoicePdfBlob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();

          // emit base64 string file encoding on success
          reader.onloadend = () => resolve((reader.result as string).replace('data:application/pdf;base64,', ''));
          // emit error
          reader.onerror = () => reject();

          reader.readAsDataURL(invoicePdfBlob);
        }),
    ),
    tap((invoicePdfString: string) => openPdfInNewTab(base64ToArrayBuffer(invoicePdfString))),
    // Handle errors in the switchMap or the tap above (invalid data from DB or incorrect encoding when converting or opening).
    tap(
      (_) => {},
      (_) => this.messageService.error('There was an error opening the invoice PDF.'),
    ),
    takeUntil(this._unsubscribe$),
  );

  defaultColumnDef: ColDef<InvoiceViewModel> = {
    ...this.agGridFactory.standardColumnDef(),
  };

  columnDefs: ColDef<InvoiceViewModel>[] = [
    {
      field: 'invoiceNumber',
      headerName: 'Invoice',
      flex: 45,
      // AG Grid v28.8 with the Server-Side Row Model does not support the built-in "select all" checkbox functionality.
      // As a result, we implement a custom header component to display and manage the checkbox manually.
      headerComponentFramework: AgGridCellWithCheckboxComponent,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      pinned: PinnedSide.LEFT,
      onCellClicked: ({ data }) => !!data?.invoiceFileId && this.viewInvoicePdf(data.id),
      cellClass: addCellClassRules({
        'ag-cell-link': (cell) => !!cell.data?.invoiceFileId,
      }),
      suppressColumnsToolPanel: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      filter: false,
      flex: 45,
      valueGetter: (row) => resolveInvoiceStatus(row.data),
      cellClass: (cell) => {
        switch (cell.value) {
          case InvoiceStatus.PAID:
            return 'invoice-status paid';
          case InvoiceStatus.OVERDUE:
            return 'invoice-status overdue';
          case InvoiceStatus.DUE:
            return 'invoice-status due';
          default:
            return 'invoice-status unpaid';
        }
      },
    },
    {
      ...NUMBER_COLUMN(),
      field: 'draftBill.arTranId',
      headerName: 'Aiwyn Draft Invoice',
      flex: 80,
      filter: false,
      onCellClicked: ({ data }) => {
        if (data?.draftBill?.arTranId && this.canViewBilling()) {
          this.viewDraftBillSubject$.next(data.draftBill.arTranId);
        }
      },
      cellClass: (cell) => (cell.value && this.canViewBilling() ? 'ag-cell-link' : ''),
    },
    {
      field: 'clientName',
      headerName: 'Client',
      flex: 160,
      cellRenderer: AgGridCellTooltipOverflowComponent,
    },
    {
      field: 'clientRef',
      headerName: 'Client ID',
      flex: 100,
      cellClass: EXCEL_STYLE_TEXT,
    },
    {
      ...DATE_COLUMN_RIGHT_ALIGNED({ [EXCEL_STYLE_DATE_MIDDLE_ENDIAN]: () => true }),
      field: 'dueDate',
      headerName: 'Due Date',
      valueFormatter: AgGridValueFormatters.DATE(this.datePipe, 'MM/dd/yyyy'),
      valueGetter: (row) =>
        row.data.dueDate?.includes('T00:00:00')
          ? row.data.dueDate
          : this.datePipe.transform(`${row.data.dueDate}Z`, 'yyyy-MM-ddTHH:mm:ss.SSS'),
      flex: 100,
    },
    {
      ...DATE_COLUMN_RIGHT_ALIGNED({ [EXCEL_STYLE_DATE_MIDDLE_ENDIAN]: () => true }),
      field: 'transactionDate',
      headerName: 'Invoice Date',
      valueFormatter: AgGridValueFormatters.DATE(this.datePipe, 'MM/dd/yyyy'),
      valueGetter: (row) =>
        row.data.transactionDate?.includes('T00:00:00')
          ? row.data.transactionDate
          : this.datePipe.transform(`${row.data.transactionDate}Z`, 'yyyy-MM-ddTHH:mm:ss.SSS'),
      flex: 100,
    },
    {
      field: 'age',
      headerName: 'Invoice Age',
      valueFormatter: ({ data }) => {
        const days = Math.floor(millisecondsToDays(data.age));
        if (days === 0) {
          return '<1 day';
        }

        return `${days} day${days === 1 ? '' : 's'}`;
      },
      flex: 45,
    },
    {
      ...NUMBER_COLUMN(),
      colId: 'amount',
      valueGetter: ({ data }) => this.getInvoiceAmount(data),
      headerName: 'Invoice Amount',
      valueFormatter: AgGridValueFormatters.CURRENCY<InvoiceViewModel>(this.currencyPipe, false, ({ data }) => data.currencyCode),
      flex: 100,
      cellClass: EXCEL_STYLE_CURRENCY,
    },
    {
      ...NUMBER_COLUMN(),
      colId: 'outstanding',
      valueGetter: ({ data }) => data.outstandingInCurrency ?? data.outstanding,
      headerName: 'Outstanding',
      valueFormatter: AgGridValueFormatters.CURRENCY<InvoiceViewModel>(this.currencyPipe, false, ({ data }) => data.currencyCode),
      flex: 100,
      cellClass: EXCEL_STYLE_CURRENCY,
    },
    {
      headerName: 'Actions',
      cellClass: addCellClassRules(['ag-grid-cell-center-aligned-vertical', 'ag-grid-cell-center-aligned']),
      cellRendererParams: {
        getTemplate: () => this.actionsTemplate,
      },
      cellRenderer: AgGridCellTemplateRendererComponent,
      pinned: PinnedSide.RIGHT,
      width: 100,
      maxWidth: 100,
      minWidth: 100,
      resizable: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
      type: EXCLUDE_COLUMN_FROM_EXPORT,
    },
  ];

  setInvoicesFilter(filter: INVOICES_TABLE_FILTER_OPTIONS): void {
    this.updateFilterSelection(filter);
  }

  viewDraftBill(draftBillId: number): void {
    this.viewDraftBillSubject$.next(draftBillId);
  }

  viewInvoicePdf(invoiceId: number): void {
    this.trackService.trackWorkflow(InvoicesStep.PDF_VIEWED, { invoiceId });
    this.viewInvoicePdfSubject$.next(invoiceId);
    this.viewInvoicePdf$.subscribe();
  }

  resendInvoice(invoice: InvoiceViewModel): void {
    const invoiceStatus = resolveInvoiceStatus(invoice);
    this.trackService.trackWorkflow(InvoicesStep.RESEND_DIALOG_OPENED, { invoiceId: invoice.id, invoiceStatus });
    this.dialog
      .open(ResendInvoiceDialogComponent, {
        data: { invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber, invoiceStatus },
        viewContainerRef: this.viewContainerRef,
      })
      .afterClosed()
      .pipe(
        tap((sent) => {
          if (sent) {
            this.messageService.success('Invoice has been successfully resent.');
          }
        }),
      )
      .subscribe();
  }

  async openReverseInvoiceConfirmationDialog(invoiceId: number, invoice?: InvoiceViewModel): Promise<void> {
    const invoiceStatus = invoice ? resolveInvoiceStatus(invoice) : '';
    const trackingPayload = { invoiceId, invoiceStatus };
    this.trackService.trackWorkflow(InvoicesStep.REVERSAL_DIALOG_OPENED, trackingPayload);
    // onSubmit fires from inside the reversal dialog when the user confirms — captured here so
    // SUCCEEDED / FAILED terminals can stamp the elapsed milliseconds back at this call site.
    let submittedAt = 0;

    const success = await this.invoiceReversalDialogs.openInvoiceReversalDialog(invoiceId, {
      onSubmit: () => {
        submittedAt = performance.now();
        this.trackService.trackWorkflow(InvoicesStep.REVERSAL_SUBMITTED, trackingPayload);
      },
      onSuccess: () =>
        this.trackService.trackWorkflow(InvoicesStep.REVERSAL_SUCCEEDED, {
          ...trackingPayload,
          operationDurationMs: performance.now() - submittedAt,
        }),
      onFailure: (err: unknown) =>
        this.trackService.trackWorkflow(InvoicesStep.REVERSAL_FAILED, {
          ...trackingPayload,
          ...extractFailureContext(err),
          operationDurationMs: performance.now() - submittedAt,
        }),
      onRejected: () => this.trackService.trackWorkflow(InvoicesStep.REVERSAL_REJECTED, trackingPayload),
      onCancel: () => this.trackService.trackWorkflow(InvoicesStep.REVERSAL_CANCELLED, trackingPayload),
    });

    if (success) {
      this.reverse.emit();

      if (this.agGrid?.api && this.datasource) {
        this.datasource.resetDatasource(this.agGrid.api);
      }
    }
  }

  createPayment(invoice: InvoiceViewModel): void {
    this.drawerInvoiceId.set(invoice.id);
    this.drawerClientId.set(invoice.clientId);
    this.drawerOutstandingAmount.set(invoice.outstandingInCurrency ?? invoice.outstanding);
    this.drawerCurrencyCode.set(invoice.currencyCode);
    this.drawerInvoiceNumber.set(invoice.invoiceNumber);

    this.drawerService.openWithTemplate(this.createPaymentDrawerTemplate, this.viewContainerRef, {
      width: '412px',
    });
  }

  onPaymentCreated(): void {
    if (this.agGrid?.api && this.datasource) {
      this.datasource.resetDatasource(this.agGrid.api);
    }
  }

  /** open invoice in client portal. The Staff user will be logged into the client portal and their actions will be audited */
  navigateToClientPortal(data: InvoiceViewModel): void {
    this.encryptApiService
      .generateAdminPaymentUrl(this.authService.getStaffId(), data.clientId, [`${data.id}`], [this.authService.getUid()])
      .pipe(
        tap(
          (encryptedUrl) => window.open(encryptedUrl),
          (error) => console.error(`Error generating admin invoice url ${error}`),
        ),
        take(1),
        takeUntil(this._unsubscribe$),
      )
      .subscribe();
  }

  constructor(
    private readonly apollo: Apollo,
    private readonly store: Store,
    private readonly authService: AuthService,
    private readonly currencyPipe: CurrencyPipe,
    private readonly invoicesApiService: InvoicesApiService,
    private readonly router: Router,
    private readonly encryptApiService: EncryptApiService,
    private readonly dialog: MatDialog,
    private readonly invoiceReversalDialogs: InvoiceReversalDialogService,
    private readonly billingPermissions: BillingPermissionsService,
    private readonly messageService: MessageService,
    private readonly pluralize: PluralizePipe,
    private readonly invoicesDialogs: InvoicesDialogsService,
    private readonly datePipe: DatePipe,
    private readonly trackService: InvoicesTrackService,
  ) {
    this.currentStaffId = this.authService.getStaffId();
    this.viewDraftBill$.subscribe();
  }

  ngOnInit(): void {
    this.datasource = new BridgeMarshalledDatasource<Invoice, InvoiceViewModel>(this.apollo, this.query, this.datasourceMarshallFn);
    this.setSearchValue$.subscribe();
  }

  onGridReady(gridOptions: GridReadyEvent): void {
    this.gridApi$.next(gridOptions.api);
    this.columnApi$.next(gridOptions.columnApi);

    this.noRowsOverlayMessage$ = combineLatest([
      this.onModelUpdated$.asObservable(),
      this.gridApi$.pipe(ignoreNullish()),
      this.datasource.requestStatus$,
      this.columnFilterApplied$,
    ]).pipe(
      map(([_, gridApi, status, filterApplied]) => {
        gridApi.hideOverlay();
        if (status === LoadableStatus.ERROR) {
          this.noRowsOverlayMessageSubject$.next(this.noRowsMessages.error);
          gridApi.showNoRowsOverlay();
        }

        const nodes = gridApi.getRenderedNodes();
        if (status === LoadableStatus.LOADED && nodes.length === 0) {
          if (this.datasource.isFiltered() || filterApplied) {
            this.noRowsOverlayMessageSubject$.next(this.noRowsMessages.filter);
            gridApi.showNoRowsOverlay();
          } else {
            this.noRowsOverlayMessageSubject$.next(this.noRowsMessages.empty);
            gridApi.showNoRowsOverlay();
          }
        }
      }),
      takeUntil(this._unsubscribe$),
    );
    this.searchValue$.pipe(takeUntil(this._unsubscribe$)).subscribe((searchValue) => {
      if (searchValue) {
        const filter: AgGridFilterAggregate = {
          operator: WhereAggregateOperator.OR,
          conditions: [
            { column: 'invoiceNumber', filter: { filterType: 'text', type: 'contains', filter: searchValue } },
            { column: 'clientName', filter: { filterType: 'text', type: 'contains', filter: searchValue } },
            { column: 'clientRef', filter: { filterType: 'text', type: 'contains', filter: searchValue } },
          ],
        };
        const formattedFilterValue: string = searchValue.replace(/[`$,]/g, '');
        if (moment(searchValue).isValid() && searchValue.includes('/')) {
          let format = 'MM/DD/YYYY';
          let endOf: 'day' | 'month' = 'day';
          const [month, day, year] = searchValue.split('/');
          if (month && day && year) {
            format = `${month.length === 1 ? 'M' : 'MM'}/${day.length === 1 ? 'D' : 'DD'}/${year.length === 2 ? 'YY' : 'YYYY'}`;
          }
          if (month && day && !year) {
            endOf = 'month';
            format = `${month.length === 1 ? 'M' : 'MM'}/${day.length === 2 ? 'YY' : 'YYYY'}`;
          }
          const from = moment(searchValue, format);
          if (from) {
            const fromDateString = from.format(DATE_FORMAT);
            const toDateString = from.endOf(endOf).format(DATE_FORMAT);
            filter.conditions.push({
              column: 'dueDate',
              filter: { filterType: 'date', type: 'inRange', filter: fromDateString, filterTo: toDateString },
            });
          }
        } else if (!Number.isNaN(+formattedFilterValue)) {
          const formattedNumberFilterValue: string = (+formattedFilterValue).toFixed(2);
          filter.conditions.push(
            { column: 'amount', filter: { filterType: 'number', type: 'equals', filter: formattedNumberFilterValue } },
            { column: 'outstanding', filter: { filterType: 'number', type: 'equals', filter: formattedNumberFilterValue } },
          );
        }
        this.datasource.addExternalFilter('search', filter);
      } else {
        this.datasource.removeExternalFilter('search');
      }
      this.searchBarControl.setValue(searchValue, { emitEvent: false });
      this.agGrid.api.onFilterChanged();
    });
    this.invoicesFilter$.pipe(take(1)).subscribe((filter) => {
      if (filter !== INVOICES_TABLE_FILTER_OPTIONS.ALL) {
        const filterConfig = this.getFilterConfig(filter);
        this.datasource.addExternalFilter(filter, filterConfig);
      }
    });
    this.invoicesFilter$.pipe(takeUntil(this._unsubscribe$), pairwise()).subscribe(([previousFilter, nextFilter]) => {
      if (nextFilter !== INVOICES_TABLE_FILTER_OPTIONS.ALL) {
        const filterConfig = this.getFilterConfig(nextFilter);
        this.datasource.addExternalFilter(nextFilter, filterConfig);
      }
      this.datasource.removeExternalFilter(previousFilter);
      this.agGrid.api.onFilterChanged();
    });
    this.noRowsOverlayMessage$.subscribe();
    this._agGridStateService = new AgGridStateService(
      {
        tableName: this.id,
        rememberColumnState: true,
        rememberColumnSort: true,
        rememberFilterModel: true,
      },
      this.store,
      this.agGrid,
    );
    this._agGridStateService.getAllEventListeners().pipe(takeUntil(this._unsubscribe$)).subscribe();
    gridOptions.api.setServerSideDatasource(this.datasource);
  }

  getFilterConfig(filter: INVOICES_TABLE_FILTER_OPTIONS): AgGridFilterModel {
    switch (filter) {
      case INVOICES_TABLE_FILTER_OPTIONS.PAID:
        return { column: 'isPaid', filter: { filterType: 'boolean', type: 'equals', filter: true } };
      case INVOICES_TABLE_FILTER_OPTIONS.UNPAID:
        return { column: 'isPaid', filter: { filterType: 'boolean', type: 'equals', filter: false } };
      case INVOICES_TABLE_FILTER_OPTIONS.OVERDUE:
        return { column: 'isOverdue', filter: { filterType: 'boolean', type: 'equals', filter: true } };
    }
  }

  // Selection handling
  private onSelectionChanged(event: SelectionChangedEvent): void {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data as InvoiceViewModel);
    this.selectedInvoices.set(selectedData);
    if (this.suppressProgrammaticSelection) {
      this.suppressProgrammaticSelection = false;
      return;
    }
    this.trackService.trackWorkflow(InvoicesStep.ROW_SELECTION_CHANGED, { selectedCount: selectedData.length });
  }

  private onFilterChanged(event: FilterChangedEvent): void {
    const model = event.api.getFilterModel() ?? {};
    this.columnFilterApplied$.next(Object.keys(model).length > 0);
    this.trackService.setColumnFilters(model);

    const previousColumns = new Set(Object.keys(this.lastFilterModel));
    const currentColumns = new Set(Object.keys(model));
    const changedColumns = new Set<string>();
    for (const columnId of currentColumns) {
      if (JSON.stringify(model[columnId]) !== JSON.stringify(this.lastFilterModel[columnId])) {
        changedColumns.add(columnId);
      }
    }
    for (const columnId of previousColumns) {
      if (!currentColumns.has(columnId)) {
        changedColumns.add(columnId);
      }
    }
    this.lastFilterModel = { ...model };
    if (!this.trackingArmed) {
      return;
    }
    for (const columnId of changedColumns) {
      this.trackService.trackWorkflow(InvoicesStep.COLUMN_FILTER_CHANGED, {
        columnId,
      });
    }
  }

  private onSortChanged(event: SortChangedEvent): void {
    const sortedColumn = event.columnApi
      .getColumnState()
      .find((column) => column.sort === 'asc' || column.sort === 'desc');
    if (!sortedColumn) {
      const cleared = Object.entries(this.lastSortState).find(([, dir]) => dir === 'asc' || dir === 'desc');
      this.lastSortState = {};
      if (!this.trackingArmed) return;
      if (cleared) {
        this.trackService.trackWorkflow(InvoicesStep.SORT_CHANGED, { columnId: cleared[0], direction: null });
      }
      return;
    }
    const columnId = sortedColumn.colId ?? '';
    const direction = sortedColumn.sort ?? null;
    if (this.lastSortState[columnId] === direction) {
      return;
    }
    this.lastSortState = { [columnId]: direction };
    if (!this.trackingArmed) return;
    this.trackService.trackWorkflow(InvoicesStep.SORT_CHANGED, { columnId, direction });
  }

  deselectAllInvoices(): void {
    const previousCount = this.selectedInvoices().length;
    this.trackService.trackWorkflow(InvoicesStep.SELECTION_CLEARED, { previousCount });
    this.selectedInvoices.set([]);
    if (this.agGrid?.api) {
      this.suppressProgrammaticSelection = true;
      this.agGrid.api.deselectAll();
    }
  }

  // Download single invoice as file
  async downloadInvoice(invoiceId: number): Promise<void> {
    try {
      const blob = await firstValueFrom(this.invoicesApiService.getInvoicePDF(`${invoiceId}`));
      const blobUrl = generateUrlFromBlob(blob);
      downloadFileFromUrl({ href: blobUrl, download: `invoice-${invoiceId}.pdf` });
    } catch {
      this.messageService.error('Failed to download invoice PDF. Please try again.');
    }
  }

  // Handle download action from toolbar or row menu
  async handleDownloadAction(invoices: InvoiceViewModel[]): Promise<void> {
    const downloadable = invoices.filter((i) => !!i.invoiceFileId);
    this.trackService.trackWorkflow(InvoicesStep.BULK_DOWNLOAD_CLICKED, {
      selectedCount: invoices.length,
      downloadableCount: downloadable.length,
    });

    if (downloadable.length === 1) {
      await this.downloadInvoice(downloadable[0].id);
      this.deselectAllInvoices();
      return;
    }

    const invoiceIds = downloadable.map((i) => i.id);
    const confirmed = await this.invoicesDialogs.confirmBulkDownload(invoiceIds);

    if (confirmed) {
      const email = this.authService.getUid();
      this.messageService.success(`Download process has begun. You will receive an email at ${email} when the download is ready.`);
      this.deselectAllInvoices();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
